import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";

import { publicPassApi } from "../../api";
import type { GuestStatus } from "../../api";
import "../../components/ticketTemplate/ticketTemplate.css";
import "../../components/ticketTemplate/zoniqTicketTemplate.css";

type View = { title: string; text: string; done: boolean };

/** What to tell the guest after PayU sends them back (or when they reopen the link later). */
export function describeGuestStatus(status: GuestStatus, paymentResult: string | null): View {
  if (status.payment_status === "refunded") {
    return { title: "Payment refunded", text: "Your payment was refunded.", done: true };
  }
  if (status.payment_status === "pending") {
    if (paymentResult === "failed") {
      return {
        title: "Payment didn't go through",
        text: "No money was taken. Go back to the form and try again.",
        done: true,
      };
    }
    return {
      title: "Confirming your payment…",
      text: "This takes a few seconds. Please keep this page open.",
      done: false,
    };
  }
  if (status.pass_url && status.pass_state !== "void") {
    return { title: "You're in", text: "Your entry pass is ready.", done: true };
  }
  if (status.payment_status === "paid") {
    return { title: "Payment received", text: "Preparing your pass…", done: false };
  }
  return { title: "Registered", text: "You're on the list.", done: true };
}

/** Landing page after PayU: confirms the payment, then leads the guest to their pass. */
const GuestStatusPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [search] = useSearchParams();
  const paymentResult = search.get("payment");

  const statusQuery = useQuery({
    queryKey: ["guest-status", token],
    queryFn: () => publicPassApi.guestStatus(token!),
    enabled: Boolean(token),
    retry: false,
    // The sale reaches Event OS a moment after PayU confirms; keep checking until it has.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      return describeGuestStatus(data, paymentResult).done ? false : 2_500;
    },
  });

  const shell = (children: React.ReactNode) => (
    <div className="zticket-page">
      <div className="zticket-card">
        <div className="zticket-body zticket-status">{children}</div>
      </div>
    </div>
  );

  if (statusQuery.isPending) return shell(<p className="zticket-hint">Loading…</p>);
  if (statusQuery.isError || !statusQuery.data) {
    return shell(
      <>
        <h2 className="zticket-status__title">Link not valid</h2>
        <p className="zticket-status__text">Open the link from your message or email again.</p>
      </>,
    );
  }

  const status = statusQuery.data;
  const view = describeGuestStatus(status, paymentResult);
  return shell(
    <>
      {status.event_name ? <p className="zticket-hint">{status.event_name}</p> : null}
      <h2 className="zticket-status__title">{view.title}</h2>
      <p className="zticket-status__text">{view.text}</p>
      {status.pass_url && status.pass_state !== "void" ? (
        <a className="eventos-primary-btn" href={status.pass_url}>
          Open my entry pass
        </a>
      ) : null}
    </>,
  );
};

export default GuestStatusPage;
